import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { VaccineCode } from '@mona/models';
import { TerminologyService } from '@mona/pdms/data-access-terminology';
import { CustomValidators } from '@mona/shared/utils';
import { VaccinationForm } from '../../models';

/**
 * Vaccination Status Form
 */
@Component({
    selector: 'app-vaccination-form',
    templateUrl: './vaccination-form.component.html',
    styleUrls: ['./vaccination-form.component.scss'],
    standalone: false,
})
export class VaccinationFormComponent implements OnInit, AfterViewInit {
    /**
     * Form group
     */
    @Input() formGroup: FormGroup<VaccinationForm>;

    /**
     * Is bed side mode
     */
    @Input() isBedSideMode: boolean;

    /**
     * Is form active / visible
     */
    @Input() isActive: boolean;

    /**
     * Max textarea rows count size
     */
    @Input() textareaMaxRows = 40;

    /**
     * Control for vaccine search — must select a BE terminology option (no free text).
     */
    @Input() vaccineSearchText = new FormControl<string | VaccineCode>('', [
        Validators.required,
        CustomValidators.requireMatch,
    ]);

    /**
     * Keyboard enter clicked
     */
    @Output() keyboardEnterClicked: EventEmitter<void> = new EventEmitter();

    /**
     * Rendered autocomplete controls
     * Prepared as ViewChildren in case that later there will be more autocompletes
     */
    @ViewChildren(MatAutocomplete) private autocompletes: QueryList<MatAutocomplete>;

    /**
     * Current data
     */
    currentDate = new Date();

    /**
     * Search results for the autocomplete.
     * Built in ngOnInit so it uses the @Input control from the dialog, not the default field init.
     */
    foundVaccineCodes$: Observable<VaccineCode[]>;

    /**
     * Constructor
     * @param terminologyService
     */
    constructor(private terminologyService: TerminologyService) {}

    /** OnInit */
    ngOnInit() {
        this.foundVaccineCodes$ = combineLatest([
            this.vaccineSearchText.valueChanges.pipe(startWith(this.vaccineSearchText.value ?? '')),
            this.terminologyService.getVaccineCodes(),
        ]).pipe(
            map(([searchValue, vaccineCodes]) => {
                const searchText =
                    (typeof searchValue === 'string' ? searchValue : searchValue?.displayName)?.toLowerCase() ?? '';
                return vaccineCodes.filter(vaccineCode => vaccineCode.displayName.toLowerCase().includes(searchText));
            }),
        );
    }

    /** AfterViewInit */
    ngAfterViewInit() {
        if (this.formGroup?.value?.vaccinationAgainst) {
            this.vaccineSearchText.patchValue(this.formGroup?.value?.vaccinationAgainst as any);
            this.formGroup.controls.vaccinationAgainst.patchValue(this.formGroup?.value?.vaccinationAgainst);
        }
    }

    /**
     * Adds an autocomplete item as new entry
     * @param $event MatAutocompleteSelectedEvent
     */
    onNewItemAutoCompleteSelect($event: MatAutocompleteSelectedEvent) {
        this.formGroup.patchValue({ vaccinationAgainst: $event.option.value }, { emitEvent: false });
        this.autocompletes.toArray().forEach(autocomplete => {
            autocomplete.showPanel = false;
        });
    }

    /**
     * Formats the output for autocomplete
     * @param result VaccineCode
     */
    autoCompleteDisplayFn(result: VaccineCode): string {
        return result?.displayName;
    }
}
